import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { InstallPrompt } from './components/InstallPrompt';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Home } from './pages/Home';
import './App.css';

// Lazy load all route components for better performance
const About = lazy(() => import('./pages/About').then(m => ({ default: m.About })));
const Services = lazy(() => import('./pages/Services').then(m => ({ default: m.Services })));
const Products = lazy(() => import('./pages/Products').then(m => ({ default: m.Products })));
const Branches = lazy(() => import('./pages/Branches').then(m => ({ default: m.Branches })));
const Blog = lazy(() => import('./pages/Blog').then(m => ({ default: m.Blog })));
const Contact = lazy(() => import('./pages/Contact').then(m => ({ default: m.Contact })));
const Order = lazy(() => import('./pages/Order').then(m => ({ default: m.Order })));
const OrderMedicines = lazy(() => import('./pages/OrderMedicines').then(m => ({ default: m.OrderMedicines })));
const InsurancePrescription = lazy(() => import('./pages/InsurancePrescription').then(m => ({ default: m.InsurancePrescription })));
const PrescriptionUpload = lazy(() => import('./pages/PrescriptionUpload').then(m => ({ default: m.PrescriptionUpload })));
const Careers = lazy(() => import('./pages/Careers').then(m => ({ default: m.Careers })));
const FAQs = lazy(() => import('./pages/FAQs').then(m => ({ default: m.FAQs })));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const Login = lazy(() => import('./pages/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/Register').then(m => ({ default: m.Register })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));

// Portal components - lazy loaded with prefetch hints
const PortalLayout = lazy(() => import('./pages/portal/PortalLayout').then(m => ({ default: m.PortalLayout })));
const Dashboard = lazy(() => import('./pages/portal/Dashboard').then(m => ({ default: m.Dashboard })));
const Orders = lazy(() => import('./pages/portal/Orders').then(m => ({ default: m.Orders })));
const Prescriptions = lazy(() => import('./pages/portal/Prescriptions').then(m => ({ default: m.Prescriptions })));
const Medications = lazy(() => import('./pages/portal/Medications').then(m => ({ default: m.Medications })));
const HealthWallet = lazy(() => import('./pages/portal/HealthWallet').then(m => ({ default: m.HealthWallet })));
const LoyaltyProgram = lazy(() => import('./pages/portal/LoyaltyProgram').then(m => ({ default: m.LoyaltyProgram })));
const AskPharmacist = lazy(() => import('./pages/portal/AskPharmacist').then(m => ({ default: m.AskPharmacist })));
const Profile = lazy(() => import('./pages/portal/Profile').then(m => ({ default: m.Profile })));

// Prefetch commonly used routes when browser is idle
const prefetchRoutes = () => {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => {
      // Prefetch common public routes
      import('./pages/OrderMedicines');
      import('./pages/InsurancePrescription');
      import('./pages/Login');
    });
  }
};

// Prefetch portal routes for logged-in users
const prefetchPortalRoutes = () => {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => {
      import('./pages/portal/Dashboard');
      import('./pages/portal/Medications');
      import('./pages/portal/Prescriptions');
      import('./pages/portal/Orders');
    });
  }
};

// Loading spinner component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto mb-3" />
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  </div>
);

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
            <p className="text-gray-600 mb-4">Please refresh the page or try again later.</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Layout wrapper to conditionally show Header/Footer
const Layout = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isPortalRoute = location.pathname.startsWith('/portal');
  const isAuthRoute = ['/login', '/register', '/forgot-password', '/reset-password'].includes(location.pathname);
  
  const hideLayout = isAdminRoute || isPortalRoute || isAuthRoute;
  
  return (
    <>
      {!hideLayout && <Header />}
      {children}
      {!hideLayout && <Footer />}
    </>
  );
};

function AppRoutes() {
  return (
    <Layout>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/products" element={<Products />} />
          <Route path="/branches" element={<Branches />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/order" element={<Order />} />
          <Route path="/order-medicines" element={<OrderMedicines />} />
          <Route path="/insurance-prescription" element={<InsurancePrescription />} />
          <Route path="/prescription" element={<PrescriptionUpload />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/faqs" element={<FAQs />} />
          <Route path="/admin" element={<AdminDashboard />} />
          
          {/* Auth Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          {/* Protected Portal Routes */}
          <Route path="/portal" element={
            <ProtectedRoute>
              <PortalLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/portal/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="orders" element={<Orders />} />
            <Route path="prescriptions" element={<Prescriptions />} />
            <Route path="medications" element={<Medications />} />
            <Route path="health" element={<HealthWallet />} />
            <Route path="loyalty" element={<LoyaltyProgram />} />
            <Route path="pharmacist" element={<AskPharmacist />} />
            <Route path="profile" element={<Profile />} />
          </Route>
        </Routes>
      </Suspense>
    </Layout>
  );
}

function App() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  // SERVICE WORKER TEMPORARILY DISABLED
  // The inline script in index.html will clean up old service workers
  // Once cleanup is complete across all users, we can re-enable this
  useEffect(() => {
    // Log that we're not registering SW
    console.log('[App] Service worker registration disabled for cleanup period');
    
    // Prefetch common routes when browser is idle
    prefetchRoutes();
  }, []);

  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <PrefetchManager />
            <InstallPrompt />
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

// Component to prefetch portal routes when user is logged in
function PrefetchManager() {
  const { user } = useAuth();
  
  useEffect(() => {
    if (user) {
      // User is logged in, prefetch portal routes
      prefetchPortalRoutes();
    }
  }, [user]);
  
  return null;
}

export default App;
